import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import useDropdownMenu from 'react-accessible-dropdown-menu-hook';
import PropTypes from 'prop-types';

import { rem } from '../utilities/style';
import { colors } from '../theme/colors';
import { shadow } from '../styles/box';

import Link from './Link';
import TextRenderer from './TextRenderer';
import { ReactComponent as Caret } from '../images/icons/caret-down.svg';
import { ReactComponent as IconAndroid } from '../images/icons/os-android.svg';
import { ReactComponent as IconApple } from '../images/icons/os-apple.svg';
import { ReactComponent as IconChrome } from '../images/icons/os-chrome.svg';
import { ReactComponent as IconLinux } from '../images/icons/os-linux.svg';
import { ReactComponent as IconSplashtop } from '../images/icons/os-splashtop.svg';
import { ReactComponent as IconWindows } from '../images/icons/os-windows.svg';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;

  ${({ dropdownItems }) => dropdownItems && `
    min-width: max-content;
  `}
`;

const ButtonOption = `
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(8)} ${rem(10)};
  border: 1px solid ${colors.gray['200']};
  background: ${colors.gray['50']};
  color: ${colors.gray['600']};
  line-height: 1.2;
  cursor: pointer;

  &:focus-visible,
  &:hover {
    z-index: 1;
    border-color: ${colors.blue['300']};
    background: ${colors.blue['100']};
    color: ${colors.blue['800']};
  }
`;

const ActiveButtonOption = `
  border-color: ${colors.blue['600']};
  background: ${colors.blue['500']};
  color: ${colors.white};

  &:focus-visible,
  &:hover {
    border-color: ${colors.blue['800']};
    background: ${colors.blue['800']};
    color: ${colors.white};
  }
`;

const ButtonLink = styled(Link)`
  ${ButtonOption}
  border-radius: ${rem(10)};

  ${({ dropdownItems }) => dropdownItems && `
    border-radius: ${rem(10)} 0 0 ${rem(10)};
  `}

  ${({ active }) => active && `
    ${ActiveButtonOption}
  `}

  svg {
    display: block;
    width: ${rem(16)};
    height: ${rem(16)};
    color: ${colors.blue['800']};

    ${({ active }) => active && `
      color: ${colors.white};
    `}
  }
`;

const Dropdown = styled.div`
  position: relative;
  margin-left: -1px;
`;

const DropdownButton = styled.button`
  ${ButtonOption}
  border-radius: 0 ${rem(10)} ${rem(10)} 0;

  ${({ active }) => active && `
    ${ActiveButtonOption}
  `}
`;

const StyledCaret = styled(Caret)`
  margin-top: ${rem(2)};

  ${({ open }) => (open && `
    transform: rotate(-180deg);
  `)}
`;

const Menu = styled.ul`
  ${shadow}
  position: absolute;
  z-index: 2;
  top: 100%;
  margin-top: ${rem(20)};
  left: 50%;
  transform: translateX(-50%);
  padding: ${rem(20)};
  border-radius: ${rem(10)};
  background-color: ${colors.white};
  visibility: hidden;
  opacity: 0;
  transition: opacity 200ms ease;

  &::after {
    content: '';
    position: absolute;
    z-index: -1;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(60%) rotate(45deg);
    width: ${rem(32)};
    height: ${rem(32)};
    border-radius: ${rem(8)};
    background: ${colors.white};
  }

  ${({ open }) => (open && css`
    visibility: visible;
    opacity: 1;
  `)}
`;

const MenuLink = styled(Link)`
  display: block;
  padding: ${rem(4)};
  color: ${colors.gray['600']};

  &:focus-visible,
  &:hover,
  &:active {
    color: ${colors.blue['800']};
    text-decoration: underline;
  }
`;

const iconMap = {
  windows: <IconWindows />,
  android: <IconAndroid />,
  macos: <IconApple />,
  ios: <IconApple />,
  iphone: <IconApple />,
  ipad: <IconApple />,
  chrome: <IconChrome />,
  linux: <IconLinux />,
  splashtop: <IconSplashtop />
};

const DownloadOption = ({
  className,
  icon,
  buttonText,
  buttonLink,
  dropdownText,
  dropdownItems,
  active,
}) => (
  <Container className={className} dropdownItems={dropdownItems}>
    <ButtonLink to={buttonLink} dropdownItems={dropdownItems} active={active}>
      {iconMap[icon]}
      <TextRenderer text={buttonText} />
    </ButtonLink>

    {dropdownItems && (
      <Options
        dropdownText={dropdownText}
        dropdownItems={dropdownItems}
        active={active}
      />
    )}
  </Container>
);

const Options = ({ dropdownText, dropdownItems, active }) => {
  const { buttonProps, itemProps, isOpen } = useDropdownMenu(dropdownItems.length);

  return (
    <Dropdown>
      <DropdownButton
        type="button"
        onKeyDown={buttonProps.onKeyDown}
        onClick={buttonProps.onClick}
        tabIndex={buttonProps.tabIndex}
        ref={buttonProps.ref}
        aria-haspopup={buttonProps['aria-haspopup']}
        aria-expanded={buttonProps['aria-expanded']}
        active={active}
      >
        <TextRenderer text={dropdownText} />
        <StyledCaret open={isOpen} />
      </DropdownButton>

      <Menu open={isOpen} role="menu">
        {dropdownItems.map(({ text, link }, index) => (
          <MenuLink
            key={index} // eslint-disable-line react/no-array-index-key
            role="menuitem"
            onKeyDown={itemProps[index].onKeyDown}
            tabIndex={itemProps[index].tabIndex}
            ref={itemProps[index].ref}
            to={link}
          >
            <TextRenderer text={text} />
          </MenuLink>
        ))}
      </Menu>
    </Dropdown>
  );
};

DownloadOption.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.string,
  buttonText: PropTypes.string,
  buttonLink: PropTypes.string,
  dropdownText: PropTypes.string,
  dropdownItems: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.string
    })
  ),
  active: PropTypes.bool,
};

export default styled(DownloadOption)();
